import {
	createSlice,
	createAsyncThunk,
	isFulfilled,
	isPending,
} from '@reduxjs/toolkit'
import { io } from 'socket.io-client'
import { toastHelper } from '../util/toastHelper.js'

// Base URL and API version for auth-related endpoints
const REQUEST_URL = `${process.env.REACT_APP_REQUEST_URL}/api`
const API_VERSION = 'v1'

// Async thunk: Login or register the user
export const auth = createAsyncThunk('api/auth', async (data) => {
	try {
		const res = await fetch(`${REQUEST_URL}/${API_VERSION}/auth/auth`, {
			method: 'POST',
			credentials: 'include',
			headers: {
				Accept: 'application/json',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify(data),
		})
		return await res.json()
	} catch (err) {
		console.log(err.message)
	}
})

// Async thunk: Check if user session is authenticated
export const checkAuth = createAsyncThunk('api/checkAuth', async () => {
	try {
		const res = await fetch(
			`${REQUEST_URL}/${API_VERSION}/auth/check-auth`,
			{
				method: 'GET',
				credentials: 'include',
				headers: {
					Accept: 'application/json',
					'Content-Type': 'application/json',
				},
			}
		)
		return await res.json()
	} catch (err) {
		console.log(err.message)
	}
})

// Async thunk: Google login (redirect flow)
export const googleAuth = createAsyncThunk(
	'auth/googleRedirectLogin',
	async (code) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/auth/google-auth`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						code,
					}),
				}
			)
			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk: Facebook login
export const facebookAuth = createAsyncThunk(
	'auth/facebook',
	async (fbResponse) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/auth/facebook-auth`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						accessToken: fbResponse.accessToken,
						userID: fbResponse.userID,
					}),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk: Verify email with code
export const verifyEmail = createAsyncThunk(
	'auth/verifyEmail',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/auth/verify-email`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					credentials: 'include',
					body: JSON.stringify(data),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk: Send verification code to current email
export const sendVerificationCode = createAsyncThunk(
	'auth/sendVerificationCode',
	async (email) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/auth/send-verification-email`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ email }),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk: Send verification code for new email address
export const sendVerificationCodeNewEmail = createAsyncThunk(
	'auth/sendVerificationCodeNewEmail',
	async (newEmail) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/accounts/send-verification-code-new-email`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ newEmail }),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk: Update profile details
export const updateProfile = createAsyncThunk(
	'auth/updateProfile',
	async (updates) => {
		try {
            const fileData = new FormData()
            for(const key in updates){
                if(typeof updates[key] !== 'string' && !updates[key]){
                    continue
                }
                if(key === 'imageMediaMap'){
                    // in case of multiple images
                    for(const k in updates.imageMediaMap){
                        fileData.append('images', updates.imageMediaMap[k])
                    }
                } else {
                    const data = Array.isArray(updates[key]) ? 
                        JSON.stringify(updates[key]) : updates[key]
                    fileData.append(String(key), data)
                }
            }

			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/accounts/update-profile`,
				{
					method: 'PATCH',
					credentials: 'include',
					// headers: {
					// 	'Content-Type': 'application/json',
					// },
					// body: JSON.stringify({ updates }),
                    body: fileData,
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Async thunk: Logout user
export const logout = createAsyncThunk('auth/logout', async () => {
	try {
		await fetch(`${REQUEST_URL}/${API_VERSION}/auth/logout`, {
			method: 'POST',
			credentials: 'include',
		})
	} catch (err) {
		console.log(err.message)
	}
})

// Async thunk: Forgot password (send reset link)
export const forgotPassword = createAsyncThunk(
	'auth/forgotPassword',
	async (email) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/accounts/forgot-password`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ email: email }),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)
// Async thunk: Reset tutorials for the user
export const resetTutorials = createAsyncThunk(
	'auth/resetTutorials',
	async () => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/tutorials/reset`,
				{
					method: 'DELETE',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
				}
			)

			const result = await res.json()
			if (!res.ok) {
				throw new Error(result.error || 'Failed to reset tutorials')
			}
			return result
		} catch (err) {
			throw err
		}
	}
)

// Async thunk: Reset password with token
export const resetPassword = createAsyncThunk(
	'auth/resetPassword',
	async ({ token, password }) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/accounts/reset-password/${token}`,
				{
					method: 'PATCH',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ password }),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const deleteAccount = createAsyncThunk(
	'auth/deleteAccount',
	async (password) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/accounts/delete-account`,
				{
					method: 'DELETE',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({ password }),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const registerUsingActivationCode = createAsyncThunk(
	'auth/registerUsingActivationCode',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/account-activation/register-using-activation-code`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(data),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

export const vendorSignUpWithActivationCode = createAsyncThunk(
	'auth/vendorSignUpWithActivationCode',
	async (data) => {
		try {
			const res = await fetch(
				`${REQUEST_URL}/${API_VERSION}/account-activation/vendor-staff-code-registration`,
				{
					method: 'POST',
					credentials: 'include',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify(data),
				}
			)

			const result = await res.json()
			return result
		} catch (err) {
			console.log(err.message)
		}
	}
)

// Initial auth slice state
const initialState = {
	user: null,
	isAuthenticated: false,
	checkAuthLoading: true,
	accountType: '',
	loading: false,
	socket: null,
	onlineUsers: [],
	userStatuses: {}, // {userId: 'online' | 'away' | 'offline'}
	isVerifyingEmailProfile: false,
	subscriptionDetails: null,
    settingsTab: 0,
    profileModalOpen: false,
	intercomToken: null,
}

// User/auth slice managing auth state, socket connections, and profile actions
const userSlice = createSlice({
	name: 'auth',
	initialState,
	reducers: {
        setProfileModalOpen: (state, { payload }) => {
            state.profileModalOpen = payload
        },

		// Create socket connection if authenticated
		connectSocket: (state) => {
			if (state.socket?.connected || !state.isAuthenticated) return

			const socket = io(process.env.REACT_APP_REQUEST_URL, {
				query: {
					userId: state.user._id,
				},
				path: '/ws',
				withCredentials: true,
			})

			socket.on('connect', () => {
				console.log('Socket connected:', socket.id)
			})

			state.socket = socket
		},

		setOnlineUsers: (state, { payload }) => {
			state.onlineUsers = payload
		},

		setUserStatuses: (state, { payload }) => {
			state.userStatuses = payload
		},

		// Disconnect socket and clear ref
		disconnectSocket: (state) => {
			if (state.socket) {
				state.socket.disconnect()
				state.socket = null
			}
		},

		// Toggle email profile verification state
		setIsVerifyingEmailProfile: (state, { payload }) => {
			state.isVerifyingEmailProfile = payload
		},

        setSettingsTab: (state, { payload }) => {
			state.settingsTab = payload
		},
	},

	extraReducers: (builder) => {
		builder
			// Handle fulfilled cases for each thunk (login, check auth, etc.)
			.addCase(auth.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Login failed. Please try again',
						'error'
					)
					return
				}

				toastHelper(payload?.message || 'Login Success', 'success')
				state.intercomToken = payload.intercomToken || null
				if (payload?.mode === 'register' || payload?.verify) {
					window.location.assign('/verify-email')
				} else if (payload?.user?.isSuperAdmin) {
					window.location.assign('/admin')
				} else {
					window.location.assign('/onboarding-account')
				}
			})

			// Handles checking if the user is still authenticated (session check)
			.addCase(checkAuth.fulfilled, (state, { payload }) => {
				state.checkAuthLoading = false
				if (!payload?.success) {
					userSlice.caseReducers.disconnectSocket(state)
					state.isAuthenticated = false
					state.user = null
					state.subscriptionDetails = null
					state.intercomToken = null
					return
				}
				state.isAuthenticated = true
				state.user = payload.user
				state.accountType = payload.user.__t
				state.subscriptionDetails = payload.subscriptionDetails || null
				state.intercomToken = payload.intercomToken || null
				if (!state.socket) userSlice.caseReducers.connectSocket(state)
			})

			// Handles successful email verification
			.addCase(verifyEmail.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
							'Email verification failed. Please try again',
						'error'
					)
					return
				}
				toastHelper(payload.message || 'Email verified', 'success')
				state.user = payload.user

				if (state.isVerifyingEmailProfile) {
					state.isVerifyingEmailProfile = false
					return
				}
				window.location.assign(
					payload.user?.isSuperAdmin ? '/admin' : '/onboarding-account'
				)
			})

			// Handles sending a verification code to current email
			.addCase(sendVerificationCode.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
							'Could not send verification email. Please try again',
						'error'
					)
					return
				}
				toastHelper(payload.message || 'Email sent', 'success')
			})

			// Handles sending a verification code to a new email
			.addCase(
				sendVerificationCodeNewEmail.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							payload?.message ||
								'Could not send verification email. Please try again',
							'error'
						)
						return
					}
					state.isVerifyingEmailProfile = true
					toastHelper(payload.message || 'Email sent', 'success')
				}
			)

			// Handles successful Google OAuth login
			.addCase(googleAuth.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
							'Google login failed. Please try again',
						'error'
					)
					return
				}
				toastHelper(payload.message, 'success')
				state.intercomToken = payload.intercomToken || null
				window.location.assign('/onboarding-account')
			})

			// Handles successful profile update
			.addCase(updateProfile.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
							'Could not save profile. Please try again',
						'error'
					)
					return
				}
				toastHelper(
					payload.message || 'Profile successfully updated',
					'success'
				)
				state.user = {
					...state.user,
					...payload.user,
				}
				state.accountType = payload.user.__t
			})

			// Handles successful logout
			.addCase(logout.fulfilled, (state, { payload }) => {
                window.location.assign('/login')
				toastHelper(payload?.message || 'Logged out', 'success')
				return { ...initialState, checkAuthLoading: false } // reset the state
			})

			// Handles sending forgot password email
			.addCase(forgotPassword.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
							'Could not send password reset email. Please try again',
						'error'
					)
					return
				}
				toastHelper(payload.message || 'Email sent', 'success')
			})

			// Handles resetting password with token
			.addCase(resetPassword.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message ||
							'Something went wrong. Please try again',
						'error'
					)
					return
				}
				toastHelper(payload.message || 'Password reset', 'success')
				window.location.assign('/login')
			})

			.addCase(deleteAccount.fulfilled, (state, { payload }) => {
				if (!payload || !payload.success) {
					toastHelper(
						payload?.message || 'Failed to delete account',
						'error'
					)
					return
				}
				toastHelper(
					payload.message || 'Account deleted successfully',
					'success'
				)
                window.location.assign('/login')
				return { ...initialState, checkAuthLoading: false } // reset the state
			})

			.addCase(
				registerUsingActivationCode.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							payload?.message ||
								'Failed to verify code',
							'error'
						)
						return
					}
				}
			)

			.addCase(
				vendorSignUpWithActivationCode.fulfilled,
				(state, { payload }) => {
					if (!payload || !payload.success) {
						toastHelper(
							payload?.message || 'Failed to register as staff',
							'error'
						)
						return
					}
					window.location.assign('/verify-email')
				}
			)

			// Dismiss toasts and set loading on pending
			.addMatcher(isPending, (state) => {
				state.loading = true
			})

			// Clear loading when any fulfilled action completes
			.addMatcher(isFulfilled, (state) => {
				state.loading = false
			})
	},
})

export const {
	connectSocket,
	setOnlineUsers,
	setUserStatuses,
	disconnectSocket,
	setIsVerifyingEmailProfile,
    setSettingsTab,
    setProfileModalOpen,
} = userSlice.actions
export default userSlice.reducer
